import { useRef } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { toast } from "@volca/ui/lib/toast";
import { useRunAgent } from "@/accounts-payable/query";
import type { ApAgentRun } from "@/accounts-payable/types";
import { getErrorMessage } from "@/core/api/errors";

const MESSAGE =
  "The agent works out new proposals from ServiceTitan as it is now. The current ones are marked superseded and can't be approved any more.";

const ALL_REFUSED =
  "ServiceTitan would refuse every proposal that changed, so none is left to approve against the current values.";

/**
 * Confirms running the agent again, since a run is the most expensive thing a
 * reviewer can start and the current proposals don't come back: the API
 * supersedes them first. A 409 (a run already in flight) shows the server's
 * message. Controlled, so the stale warning can open it from its button, its
 * menu, or right after a check that left nothing to approve (`allRefused`).
 */
export const RerunAgentDialog = ({
  run,
  businessId,
  open,
  onOpenChange,
  allRefused = false,
}: {
  run: ApAgentRun;
  businessId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  allRefused?: boolean;
}) => {
  const runAgent = useRunAgent(businessId, run.document_type);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const confirm = () =>
    runAgent.mutate(run.document_id, {
      onSuccess: () => {
        onOpenChange(false);
        toast.success("The agent is running again");
      },
      onError: (error) => {
        onOpenChange(false);
        toast.error("Couldn't re-run the agent", {
          description: getErrorMessage(error),
        });
      },
    });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !runAgent.isPending && onOpenChange(next)}
    >
      {/* Start on Cancel, the choice that changes nothing. */}
      <DialogContent initialFocus={cancelRef}>
        <DialogHeader className="pr-8">
          <DialogTitle>Re-run the agent?</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {allRefused ? `${ALL_REFUSED} ${MESSAGE}` : MESSAGE}
        </DialogDescription>
        <DialogFooter>
          <Button
            ref={cancelRef}
            variant="secondary"
            disabled={runAgent.isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={runAgent.isPending}
            loading={runAgent.isPending}
            onClick={confirm}
          >
            Re-run agent
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
